<script>
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    Button,
    LoadingState,
  },
  props: {
    config: {
      type: Array,
      default: () => [],
    },
    appTitle: {
      type: String,
      default: '',
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      required: true,
    },
  },
  emits: ['cancel'],
  data() {
    return {
      hasOpenedAtleastOnce: false,
      iframeLoading: true,
      showRedirectModal: false,
    };
  },
  computed: {
    dashboardAppContext() {
      return {
        conversation: this.currentChat,
        contact: this.$store.getters['contacts/getContact'](this.contactId),
        currentAgent: this.currentAgent,
      };
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    currentAgent() {
      const { id, name, email } = this.$store.getters.getCurrentUser;
      return { id, name, email };
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    processedConfig() {
      if (!this.config || !Array.isArray(this.config)) {
        return [];
      }

      return this.config.map(configItem => {
        if (configItem.type === 'frame' && configItem.url) {
          return {
            ...configItem,
            url: this.interpolateUrl(configItem.url),
          };
        }
        return configItem;
      });
    },
  },
  watch: {
    isVisible() {
      if (this.isVisible) {
        this.hasOpenedAtleastOnce = true;
        this.showRedirectModal = true;
      }
    },
  },
  mounted() {
    window.addEventListener('message', this.triggerEvent);
  },
  unmounted() {
    window.removeEventListener('message', this.triggerEvent);
  },
  methods: {
    triggerEvent(event) {
      if (!this.isVisible) return;
      if (event.data === 'chatwoot-dashboard-app:fetch-info') {
        this.onIframeLoad(0);
      }
    },
    getFrameId(index) {
      return `dashboard-app--frame-${this.position}-${index}`;
    },
    onIframeLoad(index) {
      // A possible alternative is to use ref instead of document.getElementById
      // However, when ref is used together with v-for, the ref you get will be
      // an array containing the child components mirroring the data source.
      const frameElement = document.getElementById(this.getFrameId(index));
      const eventData = { event: 'appContext', data: this.dashboardAppContext };
      frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
      this.iframeLoading = false;
    },
    interpolateUrl(url) {
      if (!url || !this.contact) {
        return url;
      }

      return url.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
        if (
          this.contact.custom_attributes &&
          this.contact.custom_attributes[fieldName]
        ) {
          return encodeURIComponent(this.contact.custom_attributes[fieldName]);
        }

        if (
          this.contact[fieldName] !== undefined &&
          this.contact[fieldName] !== null
        ) {
          return encodeURIComponent(this.contact[fieldName]);
        }

        return match;
      });
    },
    closeRedirectModal() {
      this.showRedirectModal = false;
      this.$emit('cancel');
    },
    openDashboardAppInNewTab() {
      const appUrl = this.processedConfig.find(
        item => item.type === 'frame' && item.url
      )?.url;
      if (appUrl) {
        window.open(appUrl, '_blank', 'noopener,noreferrer');
      }
      this.closeRedirectModal();
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="hasOpenedAtleastOnce" class="dashboard-app--container">
    <woot-modal
      :show="showRedirectModal && isVisible"
      :on-close="closeRedirectModal"
      :show-close-button="false"
    >
      <div class="flex flex-col items-center w-full max-w-[31.25rem] px-8 py-10 text-center">
        <div
          class="flex items-center gap-2 px-4 py-3 mb-8 border rounded-xl bg-n-background border-n-weak"
        >
          <span class="w-2 h-2 rounded-full bg-n-brand" />
          <span class="text-sm text-n-slate-11">{{ appTitle }}</span>
          <span class="ml-2 text-n-slate-9">x</span>
        </div>
        <h2 class="mb-2 text-3xl font-semibold text-n-slate-12">
          {{
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.OPEN_IN_NEW_TAB_MODAL.TITLE')
          }}
        </h2>
        <p class="mb-8 text-base text-n-slate-11">
          {{
            $t(
              'INTEGRATION_SETTINGS.DASHBOARD_APPS.OPEN_IN_NEW_TAB_MODAL.DESCRIPTION'
            )
          }}
        </p>
        <div class="flex items-center justify-center w-full gap-3">
          <Button
            faded
            slate
            :label="
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.OPEN_IN_NEW_TAB_MODAL.BUTTONS.CANCEL'
              )
            "
            @click="closeRedirectModal"
          />
          <Button
            icon="i-lucide-square-arrow-out-up-right"
            :label="
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.OPEN_IN_NEW_TAB_MODAL.BUTTONS.OPEN'
              )
            "
            @click="openDashboardAppInNewTab"
          />
        </div>
      </div>
    </woot-modal>
    <div
      v-for="(configItem, index) in processedConfig"
      :key="index"
      class="dashboard-app--list"
    >
      <LoadingState
        v-if="iframeLoading && !showRedirectModal"
        :message="$t('DASHBOARD_APPS.LOADING_MESSAGE')"
        class="dashboard-app_loading-container"
      />
      <iframe
        v-if="configItem.type === 'frame' && configItem.url && !showRedirectModal"
        :id="getFrameId(index)"
        :src="configItem.url"
        @load="() => onIframeLoad(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app--list iframe {
  border: 0;
}
.dashboard-app_loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
</style>
